import { createSlice } from "@reduxjs/toolkit";

import { ReduxReducer } from "../../../../interface";
import { Orders } from "../../interface";

import { ParkingFilter, State } from "./interface";

export const ALL_PARKING_EXECUTOR_STATUSES = [
	"available",
	"busy",
	"dinner",
	"home",
	"on_order",
	"closed",
];

export const ALL_PARKING_ONLINE: ParkingFilter["online"] = ["online", "offline"];
export const ALL_PARKING_WORKING: ParkingFilter["workingStatus"] = [
	"working",
	"notWorking",
];

export const defaultParkingExecutorFilters = {
	statuses: [...ALL_PARKING_EXECUTOR_STATUSES],
	online: [...ALL_PARKING_ONLINE],
	workingStatus: [...ALL_PARKING_WORKING],
	isYourOrderStatus: true,
};

export const normalizeParkingFilterIds = (ids: unknown): number[] => {
	if (!Array.isArray(ids) || ids.length === 0) return [];
	if (ids[0] === "all") return [];

	return ids
		.map((id) => Number(id))
		.filter((id) => Number.isInteger(id) && id > 0);
};

export const needsParkingBranchFill = (ids: unknown): boolean => {
	if (!Array.isArray(ids) || ids.length === 0) return true;
	if (ids[0] === "all") return true;

	return ids.every(
		(id) => typeof id !== "number" || !Number.isInteger(id) || id <= 0,
	);
};

export const parkingFilterAllowsId = (
	selectedIds: unknown,
	id?: number | null,
): boolean => {
	if (id == null) return false;

	const ids = Array.isArray(selectedIds) ? selectedIds : [];
	if (ids.length === 0 || ids[0] === "all") return true;

	const numericId = Number(id);
	return ids.some((selected) => Number(selected) === numericId);
};

const isLegacyShowAllFilter = (criteria: ParkingFilter): boolean => {
	const statuses = criteria?.statuses ?? [];
	const online = criteria?.online ?? [];
	const workingStatus = criteria?.workingStatus ?? [];

	return (
		statuses.length === 0 &&
		online.length === 0 &&
		workingStatus.length === 0
	);
};

/**
 * Empty status/shift arrays were stored as “show all”. Expand them so
 * checkboxes match the list (everything visible ⇒ everything selected).
 * `["all"]` and string ids in branch filters are normalized too.
 */
export const sanitizeParkingWidgetFilter = (
	criteria: ParkingFilter,
): ParkingFilter => {
	if (!criteria) return criteria;

	const normalized: ParkingFilter = {
		...criteria,
		companyIds: normalizeParkingFilterIds(criteria.companyIds),
		taxiServiceIds: normalizeParkingFilterIds(criteria.taxiServiceIds),
		executorGroupIds: normalizeParkingFilterIds(criteria.executorGroupIds),
		taxiServiceIdsPosition: normalizeParkingFilterIds(
			criteria.taxiServiceIdsPosition,
		),
		statuses: Array.isArray(criteria.statuses) ? [...criteria.statuses] : [],
		online: Array.isArray(criteria.online) ? [...criteria.online] : [],
		workingStatus: Array.isArray(criteria.workingStatus)
			? [...criteria.workingStatus]
			: [],
		isYourOrderStatus: Boolean(criteria.isYourOrderStatus),
	};

	if (!isLegacyShowAllFilter(normalized)) return normalized;

	return {
		...normalized,
		statuses: [...defaultParkingExecutorFilters.statuses],
		online: [...defaultParkingExecutorFilters.online],
		workingStatus: [...defaultParkingExecutorFilters.workingStatus],
		isYourOrderStatus: defaultParkingExecutorFilters.isYourOrderStatus,
	};
};

const initialState: State = {
	criteria: {
		companyIds: [],
		taxiServiceIds: [],
		executorGroupIds: [],
		taxiServiceIdsPosition: [],
		...defaultParkingExecutorFilters,
		query: "",
	},
	sort: {
		column: "",
		type: "asc",
	},
};

const setParkingsQuery: ReduxReducer<State, string> = (state, action) => {
	state.criteria.query = action.payload;
};

const setParkingsPosition: ReduxReducer<State, number[]> = (state, action) => {
	state.criteria.taxiServiceIdsPosition = action.payload;
};

const setParkingsFilter: ReduxReducer<State, ParkingFilter> = (
	state,
	action,
) => {
	state.criteria = sanitizeParkingWidgetFilter(action.payload);
};

const setParkingsSort: ReduxReducer<State, State["sort"]> = (state, action) => {
	state.sort = action.payload;
};

const orderParkingFilters = createSlice({
	name: Orders.ConstantOrdersSlice.PARKING_FILTERS,
	initialState,
	reducers: {
		setParkingsSort,
		setParkingsQuery,
		setParkingsPosition,
		setParkingsFilter,
	},
});

export const orderParkingFiltersAction = orderParkingFilters.actions;
export const orderParkingFiltersReducer = orderParkingFilters.reducer;
export type orderParkingFiltersState = State;

export default orderParkingFilters;
